import React, { useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Link, router } from '@inertiajs/react';
import { Plus, Edit, Trash2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';

type Faq = {
  id: number;
  question: string;
  answer: string;
  category: string;
  position: number;
  published: boolean;
};

type Props = {
  faqs: Faq[];
};

export default function FaqsIndex({ faqs }: Props) {
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const groupedFaqs = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {} as Record<string, Faq[]>);

  const handleDelete = () => {
    if (deleteId) {
      router.delete(`/admin/faqs/${deleteId}`, {
        onSuccess: () => setDeleteId(null),
      });
    }
  };

  return (
    <AdminLayout title="FAQs">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">FAQs</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage frequently asked questions
            </p>
          </div>
          <Button asChild>
            <Link href="/admin/faqs/new">
              <Plus className="h-4 w-4 mr-2" />
              Add FAQ
            </Link>
          </Button>
        </div>

        {/* FAQs by Category */}
        {Object.keys(groupedFaqs).length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-500">No FAQs found. Create your first FAQ to get started.</p>
          </div>
        ) : (
          Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
            <div key={category} className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-medium text-gray-900 capitalize">
                  {category}
                </h2>
              </div>
              <div className="divide-y divide-gray-200">
                {categoryFaqs.map((faq) => (
                  <div key={faq.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 pr-4">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-base font-medium text-gray-900">
                            {faq.question}
                          </h3>
                          <Badge variant={faq.published ? "default" : "secondary"}>
                            {faq.published ? "Published" : "Draft"}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {faq.answer}
                        </p>
                        <div className="mt-2 text-xs text-gray-500">
                          Position: {faq.position}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          asChild
                        >
                          <Link href={`/admin/faqs/${faq.id}/edit`}>
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Link>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteId(faq.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete FAQ</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this FAQ? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}